import { useEffect, useMemo, useState } from "react";

import { isLoggedIn, ServiceError, SpinnerCenter } from "@shapr3d/frontend-lib";
import { useLocation } from "react-router-dom";

import SignIn from "../components/SignIn/SignIn";
import ZendeskEmailVerify from "../components/ZendeskEmailVerify/ZendeskEmailVerify";
import ZendeskLogOut from "../components/ZendeskLogOut/ZendeskLogOut";
import useNavigation from "../hooks/navigation";
import { useLogin, useLogout, useResendVerificationEmail, useZendeskToken } from "../hooks/useAuth";
import { useTracker } from "../hooks/useTracker";
import { SearchParam, ServiceErrorCode } from "../types";

enum ZendeskError {
  None,
  EmailVerification,
  Unknown,
}

const SignInZendeskContainer = () => {
  const [zendeskError, setZendeskError] = useState(ZendeskError.None);
  const location = useLocation();
  const tracker = useTracker();
  const navigation = useNavigation();

  const appTrackingID = useMemo(() => tracker.getTrackingID(), [tracker]);

  const {
    mutateAsync: mutateLogin,
    error: loginError,
    isPending: isLoginPending,
  } = useLogin({ appTrackingID });

  const { mutateAsync: mutateLogout } = useLogout();

  const { mutateAsync: mutateResendVerificationEmail } = useResendVerificationEmail();
  const {
    data: zendeskTokenData,
    isFetching: isZendeskTokenFetching,
    error: zendeskTokenError,
  } = useZendeskToken();

  useEffect(() => {
    if (zendeskTokenData?.zendeskToken) {
      const urlQuery = new URLSearchParams(location.search);
      navigation.goToZendesk(
        zendeskTokenData.zendeskToken,
        urlQuery.get(SearchParam.ReturnTo) ?? ""
      );
    }
  }, [zendeskTokenData, navigation, location]);

  useEffect(() => {
    if (!zendeskTokenError) {
      return;
    }

    if (zendeskTokenError instanceof ServiceError) {
      if (zendeskTokenError.hasCode(ServiceErrorCode.EmailNotVerified)) {
        setZendeskError(ZendeskError.EmailVerification);
      } else {
        setZendeskError(ZendeskError.Unknown);
      }
    } else {
      mutateLogout();
    }
  }, [zendeskTokenError, mutateLogout]);

  const signIn = async (email: string, password: string, captcha?: string) => {
    try {
      await mutateLogin({ email, password, captcha });
    } catch (error) {
      // This is to not throw Unhandled Promise Rejection error
    }
  };

  return (
    <>
      {!isLoggedIn() && (
        <SignIn
          submit={signIn}
          inProgress={isLoginPending}
          hideRegistration
          loginError={loginError}
        />
      )}
      {isZendeskTokenFetching && <SpinnerCenter />}
      {zendeskError === ZendeskError.EmailVerification && (
        <ZendeskEmailVerify onResendClick={mutateResendVerificationEmail} />
      )}
      {zendeskError === ZendeskError.Unknown && (
        <ZendeskLogOut errorMessage="Unknown error happened." />
      )}
    </>
  );
};

export default SignInZendeskContainer;
